import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { pageTitle } from "ember-page-title";
import type Owner from "@ember/owner";
import type SequencerService from "#services/sequencer.ts";
import { Song } from "#models/song.ts";
import { Channel } from "#models/channel.ts";
import { Step } from "#models/step.ts";
import { Sound } from "#models/sound.ts";
import { MAX_BPM, MIN_BPM } from "#services/sequencer.ts";

const eq = <T,>(a: T, b: T): boolean => a === b;

const str = (channel: Channel): string => channel.toString();

export default class SandboxSequencerTemplateComponent extends Component {
  @service declare sequencer: SequencerService;

  demoSong: Song = new Song({
    name: "Sequencer Test Pattern",
    tempo: 120,
    channels: [
      new Channel({
        sound: Sound.findById("kick")!,
        volume: 1.0,
        steps: Array.from({ length: 16 }, (_, i) => new Step({ velocity: i % 4 === 0 ? 1.0 : 0 })),
      }),
      new Channel({
        sound: Sound.findById("snare")!,
        volume: 0.9,
        steps: Array.from(
          { length: 16 },
          (_, i) => new Step({ velocity: i === 4 || i === 12 ? 1.0 : 0 }),
        ),
      }),
      new Channel({
        sound: Sound.findById("hihat_closed")!,
        volume: 0.6,
        steps: Array.from({ length: 16 }, (_, i) => new Step({ velocity: i % 2 === 0 ? 0.5 : 0 })),
      }),
      new Channel({
        sound: Sound.findById("cowbell")!,
        volume: 0.7,
        // 3-step polyrhythm track
        steps: [new Step({ velocity: 1.0 }), new Step({ velocity: 0 }), new Step({ velocity: 0 })],
      }),
    ],
  });

  constructor(owner: Owner, args: object) {
    super(owner, args);
    this.sequencer.loadSong(this.demoSong);
  }

  get song(): Song {
    return this.sequencer.song ?? this.demoSong;
  }

  get currentTick(): number | null {
    return this.sequencer.currentTick;
  }

  get isPlaying(): boolean {
    return this.sequencer.isPlaying;
  }

  get intervalMs(): number {
    return Math.round(this.sequencer.intervalMs);
  }

  increaseTempo = () => {
    this.song.tempo = Math.min(MAX_BPM, this.song.tempo + 5);
  };

  decreaseTempo = () => {
    this.song.tempo = Math.max(MIN_BPM, this.song.tempo - 5);
  };

  setTempo = (bpm: number) => {
    this.song.tempo = bpm;
  };

  handleTogglePlay = () => {
    this.sequencer.togglePlay();
  };

  handleStop = () => {
    this.sequencer.stop();
  };

  handleReset = () => {
    this.sequencer.reset();
  };

  handleManualTick = () => {
    this.sequencer.tick();
  };

  isCurrentStep = (channel: Channel, stepIndex: number): boolean => {
    if (this.currentTick === null || !channel.steps || channel.steps.length === 0) {
      return false;
    }
    return this.currentTick % channel.steps.length === stepIndex;
  };

  handleToggleMute = (channel: Channel) => {
    channel.isMuted = !channel.isMuted;
  };

  handlePreviewSound = (sound: Sound) => {
    this.sequencer.previewSound(sound);
  };

  willDestroy(): void {
    super.willDestroy();
    this.sequencer.stop();
  }

  <template>
    {{pageTitle "Sequencer Sandbox"}}

    <div class="space-y-6">
      <section class="space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 class="text-xl font-bold text-white flex items-center gap-3">
              Sequencer Service
            </h2>
          </div>

        </div>

        {{! Transport Controls }}
        <div class="flex flex-wrap items-center gap-3 pt-4">
          <button
            type="button"
            class="px-4 py-2 font-bold text-xs rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
            {{on "click" this.handleTogglePlay}}
          >
            {{if this.isPlaying "Pause" "Play"}}
          </button>
          <button
            type="button"
            class="px-4 py-2 font-bold text-xs rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
            {{on "click" this.handleStop}}
          >
            Stop
          </button>
          <button
            type="button"
            class="px-4 py-2 font-bold text-xs rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
            {{on "click" this.handleReset}}
          >
            Reset Tick
          </button>
          <button
            type="button"
            class="px-4 py-2 font-bold text-xs rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
            {{on "click" this.handleManualTick}}
          >
            Single Step Tick
          </button>

          <div class="ml-auto flex items-center gap-2">
            <span class="text-xs text-slate-400">Tempo:</span>
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-bold rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
              {{on "click" this.decreaseTempo}}
            >
              -5
            </button>
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-bold rounded bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
              {{on "click" this.increaseTempo}}
            >
              +5
            </button>
            <div class="flex gap-1 ml-2">
              <button
                type="button"
                class="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                {{on "click" (fn this.setTempo 90)}}
              >90</button>
              <button
                type="button"
                class="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                {{on "click" (fn this.setTempo 120)}}
              >120</button>
              <button
                type="button"
                class="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                {{on "click" (fn this.setTempo 140)}}
              >140</button>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 class="text-lg font-bold text-white gap-3">
          Settings
        </h3>
        <div class="mb-6">
          <div>
            <span class="text-slate-500">this.sequencer.isPlaying:</span>
            <span class="font-mono">{{this.sequencer.isPlaying}}</span>
          </div>
          <div>
            <span class="text-slate-500">this.sequencer.currentTick:</span>
            <span class="font-mono">{{if
                (eq this.sequencer.currentTick null)
                "null"
                this.sequencer.currentTick
              }}</span>
          </div>
          <div>
            <span class="text-slate-500">this.sequencer.song.tempo:</span>
            <span class="font-mono">{{this.sequencer.song.tempo}}</span>
          </div>
          <div>
            <span class="text-slate-500">this.sequencer.intervalMs:</span>
            <span class="font-mono">{{this.sequencer.intervalMs}}</span>
          </div>
        </div>
        <h3 class="text-lg font-bold text-white gap-3">
          Channels</h3>
        <ul>
          {{#each this.sequencer.song.channels as |channel|}}
            <li class="mb-4">
              <div class="underline">
                {{str channel}}
                <button
                  type="button"
                  {{on "click" (fn this.handleToggleMute channel)}}
                  class="underline text-blue-500 px-1"
                >
                  {{if channel.isMuted "unmute" "mute"}}
                </button>
              </div>
              <ol>
                {{#each channel.steps as |step idx|}}
                  {{#let (this.isCurrentStep channel idx) as |isCurrent|}}
                    <li
                      class="inline-block font-mono
                        {{if isCurrent 'text-red-500' 'text-green-200'}}"
                    >
                      {{if step.enabled (if (eq step.velocity 1) "●" "◐") "·"}}
                    </li>
                  {{/let}}
                {{/each}}
              </ol>
            </li>
          {{/each}}
        </ul>
      </section>

    </div>
  </template>
}
